import React, { useState, useEffect } from 'react';
import {
  Hazard,
  HazardStatus,
  HazardSeverity,
  HazardDashboardStats,
  HazardCategory,
  FALLBACK_HAZARD_CATEGORIES,
} from './types';
import { hazardService } from './hazardService';
import { HazardsTable } from './components/HazardsTable';
import { CreateHazardModal } from './components/CreateHazardModal';
import { HazardDetailModal } from './components/HazardDetailModal';
import { HazardCategoriesTab } from './components/HazardCategoriesTab';

type SubmenuView = 'register' | 'active' | 'review' | 'closed' | 'categories';

interface HazardsPageProps {
  onNavigateToBowtie?: (riskId: string) => void;
}

export const HazardsPage: React.FC<HazardsPageProps> = ({ onNavigateToBowtie }) => {
  const [currentView, setCurrentView] = useState<SubmenuView>('register');
  const [hazards, setHazards] = useState<Hazard[]>([]);
  const [categories, setCategories] = useState<HazardCategory[]>(FALLBACK_HAZARD_CATEGORIES);
  const [stats, setStats] = useState<HazardDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('');
  const [selectedHazard, setSelectedHazard] = useState<Hazard | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchStats = async () => {
    try {
      const data = await hazardService.getDashboardStats();
      setStats(data);
    } catch (err: any) {
      console.error('Failed to load hazard stats:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const cats = await hazardService.getCategories();
      const merged: HazardCategory[] = [];
      const seenCodes = new Set<string>();

      for (const c of (cats || [])) {
        const key = (c.code || '').trim().toUpperCase();
        if (!seenCodes.has(key)) {
          seenCodes.add(key);
          merged.push(c);
        }
      }

      for (const f of FALLBACK_HAZARD_CATEGORIES) {
        const key = f.code.trim().toUpperCase();
        if (!seenCodes.has(key)) {
          seenCodes.add(key);
          merged.push(f);
        }
      }

      setCategories(merged);
    } catch (err: any) {
      console.error('Failed to load categories:', err);
      setCategories(FALLBACK_HAZARD_CATEGORIES);
    }
  };

  const fetchHazards = async () => {
    if (currentView === 'categories') return;

    try {
      setLoading(true);
      let statusFilter: HazardStatus[] | undefined = undefined;

      if (currentView === 'active') {
        statusFilter = ['ACTIVE', 'IDENTIFIED'];
      } else if (currentView === 'review') {
        statusFilter = ['UNDER_REVIEW'];
      } else if (currentView === 'closed') {
        statusFilter = ['CLOSED', 'RETIRED'];
      }

      const res = await hazardService.getHazards({
        status: statusFilter,
        categoryId: selectedCategory || undefined,
        severityEstimate: selectedSeverity ? (selectedSeverity as HazardSeverity) : undefined,
        searchTerm: searchTerm.trim() || undefined,
      });

      setHazards(res.items || []);
    } catch (err: any) {
      console.error('Failed to load hazards:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchHazards();
  }, [currentView, selectedCategory, selectedSeverity]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchHazards();
  };

  return (
    <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '0 24px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Top Header */}
      <div className="page-header-banner">
        <div className="badge">SAFETY MANAGEMENT</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Safety Hazard Register</h1>
            <p>Proactive hazard identification, condition to consequence analysis, and safety barrier integrity monitoring.</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
            style={{ width: 'auto', padding: '10px 18px' }}
          >
            + Identify Hazard
          </button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Register</div>
            <div className="stat-value">{stats?.totalHazards ?? '-'}</div>
            <div className="stat-sub">Identified hazards</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Active Threats</div>
            <div className="stat-value" style={{ color: '#dc2626' }}>{stats?.activeHazards ?? '-'}</div>
            <div className="stat-sub">Unmitigated/Active</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Under Review</div>
            <div className="stat-value" style={{ color: '#d97706' }}>{stats?.underReviewHazards ?? '-'}</div>
            <div className="stat-sub">Analysis in progress</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Mitigated / Controlled</div>
            <div className="stat-value" style={{ color: '#16a34a' }}>{stats?.mitigatedHazards ?? '-'}</div>
            <div className="stat-sub">Barriers verified</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Review Overdue</div>
            <div className="stat-value" style={{ color: '#b91c1c' }}>{stats?.overdueReviewCount ?? 0}</div>
            <div className="stat-sub">Past review threshold</div>
          </div>
        </div>
      )}

      {/* Submenu Navigation */}
      <div className="nav-tabs">
        {[
          { id: 'register', label: 'Hazard Register' },
          { id: 'active', label: 'Active Hazards' },
          { id: 'review', label: 'Under Review' },
          { id: 'closed', label: 'Closed & Retired' },
          { id: 'categories', label: 'Taxonomies & Categories' },
        ].map((sub) => (
          <button
            key={sub.id}
            onClick={() => { setCurrentView(sub.id as SubmenuView); fetchCategories(); }}
            className={`tab-btn ${currentView === sub.id ? 'active' : ''}`}
          >
            {sub.label}
          </button>
        ))}
      </div>

      {/* Filter and Search Bar */}
      {currentView !== 'categories' && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '8px', flex: 1, maxWidth: '480px' }}>
            <input
              type="text"
              placeholder="Search by hazard number, title, or condition..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
            />
            <button
              type="submit"
              style={{ background: '#334155', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
            >
              Search
            </button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Category:</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    [{c.code}] {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Severity:</label>
              <select
                value={selectedSeverity}
                onChange={(e) => setSelectedSeverity(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
              >
                <option value="">All Severities</option>
                <option value="CRITICAL">CRITICAL</option>
                <option value="HIGH">HIGH</option>
                <option value="MEDIUM">MEDIUM</option>
                <option value="LOW">LOW</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Main Tab Content */}
      {currentView === 'categories' ? (
        <HazardCategoriesTab onCategoryCreated={fetchCategories} />
      ) : (
        <HazardsTable
          hazards={hazards}
          loading={loading}
          onSelect={(h) => setSelectedHazard(h)}
          onRefresh={() => {
            fetchHazards();
            fetchStats();
          }}
        />
      )}

      {/* Modals */}
      {selectedHazard && (
        <HazardDetailModal
          hazardId={selectedHazard.id}
          onClose={() => setSelectedHazard(null)}
          onOpenBowtie={onNavigateToBowtie}
          onUpdated={() => {
            fetchHazards();
            fetchStats();
          }}
        />
      )}

      {showCreateModal && (
        <CreateHazardModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchHazards();
            fetchStats();
          }}
        />
      )}
    </div>
  );
};
